import {getPixelsFromRem, Show, VStack} from 'platform/foundation';

import {useId, useMemo, useRef} from 'react';
import ReactSelect, {createFilter, OnChangeValue, type SelectInstance} from 'react-select';

import {defaultTo, isNil, reject} from 'ramda';
import {isNotArray} from 'ramda-adjunct';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {MENU_MAX_HEIGHT} from '../../../constants/menuMaxHeight';
import {useComponentsTheme} from '../../../hooks/useComponentsTheme';
import {FormControlProps} from '../../../types/FormControlProps';
import {OptionTypeBase} from '../../../types/OptionTypeBase';
import {HelperText} from '../../HelperText/HelperText';
import {Label} from '../../Label/Label';
import {LabelActions} from '../../Label/LabelActions';
import {useTranslationContext} from '../../TranslationProvider/TranslationContext';
import {ChoiceCommonProps} from '../types';
import {GetItemImage, selectComponents} from '../utils/avatarChoiceComponents';
import {formatOptionFieldLabel} from '../utils/formatOptionFieldLabel';
import {getChoiceCSS} from '../utils/getChoiceCSS';
import {getFlattenedOptions} from '../utils/getFlattenedOptions';
import {reactSelectStyles} from '../utils/reactSelectStyles';
import {selectMultiOption} from '../utils/selectMultiOption';
import {ChoiceProvider} from './ChoiceProvider';

const rejectNil = reject(isNil);

interface AvatarMultiChoiceProps<ValueType>
  extends
    FormControlProps<ValueType[] | null>,
    TestIdProps,
    ChoiceCommonProps<ValueType[], ValueType> {
  horizontalMenuPlacement?: 'left' | 'right';
  labelActions?: LabelActions;
  getItemImage: GetItemImage<ValueType>;
}

export function AvatarMultiChoice<ValueType>(props: AvatarMultiChoiceProps<ValueType>) {
  const selectRef = useRef<SelectInstance<OptionTypeBase<ValueType | Nullish>, true> | null>(null);
  const t = useTranslationContext();
  const theme = useComponentsTheme();
  const id = useId();

  const isInvalid = props.isInvalid || !!props.errorMessage;

  const onSelectMount = (
    select: SelectInstance<OptionTypeBase<ValueType | Nullish>, true> | null
  ) => {
    if (select) {
      selectMultiOption(select);
    }

    selectRef.current = select;
  };

  const onMenuOpen = () => {
    if (selectRef.current) {
      selectMultiOption(selectRef.current);
    }
  };

  const handleChange = (options: OnChangeValue<OptionTypeBase<ValueType | Nullish>, true>) => {
    if (!options) {
      props.onChange?.(null);
      return;
    }

    props.onChange?.(rejectNil(options.map((v) => v.value)));
  };

  const getOptionsByValue = (value: ValueType[] | Nullish) => {
    if (isNotArray(value) || isNil(value)) {
      return null;
    }

    const flatOptions = getFlattenedOptions(props.options);

    return rejectNil(value.map((v) => flatOptions?.find((o) => o.value === v)));
  };

  const components = useMemo(
    () => ({...selectComponents<ValueType, true>({getItemImage: props.getItemImage})}),
    [props.getItemImage]
  );

  const defaultFilterOption = useMemo(
    () =>
      createFilter({
        stringify: (option) => `${option.label}`,
        ignoreCase: true,
      }),
    []
  );

  return (
    <div
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.stopPropagation();
        }
      }}
    >
      <VStack>
        <Label
          id={id}
          isRequired={props.isRequired}
          tooltip={props.tooltip}
          isRecommended={props.isRecommended}
          actions={props.labelActions}
          data-testid={suffixTestId('label', props)}
        >
          {props.label}
        </Label>
        <ChoiceProvider
          options={{
            'data-testid': props['data-testid'],
            'data-name': props.name,
            hasOptionCheckbox: props.hasOptionCheckbox,
            isInvalid,
          }}
        >
          <ReactSelect
            isMulti
            inputId={id}
            autoFocus={props.autoFocus}
            formatOptionLabel={props.formatOptionLabel ?? formatOptionFieldLabel}
            defaultValue={getOptionsByValue(props.defaultValue)}
            ref={onSelectMount}
            menuPortalTarget={props.menuInPortal ? document.body : undefined}
            value={getOptionsByValue(props.value)}
            name={props.name}
            placeholder={defaultTo(t('general.labels.select'), props.placeholder)}
            onInputChange={() => {
              if (props.menuIsOpen) {
                selectRef?.current?.inputRef?.focus();
              }
            }}
            onFocus={props.onFocus}
            onBlur={props.onBlur}
            onChange={handleChange}
            isDisabled={props.isDisabled}
            options={props.options}
            isClearable={!props.isNotClearable}
            isSearchable={props.isSearchable ?? true}
            components={components}
            hideSelectedOptions={false}
            tabSelectsValue={false}
            menuPlacement={props.menuPlacement ?? 'auto'}
            maxMenuHeight={getPixelsFromRem(props.maxMenuHeight ?? MENU_MAX_HEIGHT)}
            styles={reactSelectStyles<ValueType, true>(theme, props.horizontalMenuPlacement)}
            onMenuOpen={onMenuOpen}
            noOptionsMessage={props.noOptionsMessage}
            closeMenuOnScroll={props.closeMenuOnScroll}
            closeMenuOnSelect={props.closeMenuOnSelect}
            isLoading={props.isLoading}
            filterOption={props.filterOption ?? defaultFilterOption}
            className={getChoiceCSS(isInvalid, props.isDisabled, props.isRecommended)}
          />
        </ChoiceProvider>
        <Show when={props.errorMessage ?? props.helperText}>
          <HelperText
            errorMessage={props.errorMessage}
            helperText={props.helperText}
            data-testid={suffixTestId('helper', props)}
          />
        </Show>
      </VStack>
    </div>
  );
}
